// src/hooks/useAuth.js
import { useState, useEffect, useCallback, useRef } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { TERMS_VERSION } from "../legalVersion";

// Loads the merged profile from the slim routing doc (/users/{uid}) and the
// authoritative org-specific members doc (/organizations/{orgId}/members/{uid}).
// Handles lazy one-time migration for users created before this schema change.
async function loadFullProfile(uid) {
  const userSnap = await getDoc(doc(db, "users", uid));
  if (!userSnap.exists()) return null;

  const routing = userSnap.data();
  const orgId = routing.orgId;
  if (!orgId) return { ...routing };

  let memberSnap;
  try {
    memberSnap = await getDoc(doc(db, "organizations", orgId, "members", uid));
  } catch {
    // Rules blocked the read (e.g. stale deployed rules) — fall back to routing doc.
    return { ...routing };
  }

  if (!memberSnap.exists()) {
    // Legacy user (pre-schema-change) — migrate to members doc.
    const memberData = {
      uid,
      email: routing.email || "",
      displayName: routing.displayName || "",
      role: routing.role || "user",
      orgStatus: routing.orgStatus || "approved",
      disabled: routing.disabled || false,
      createdAt: routing.createdAt,
    };
    try {
      await setDoc(doc(db, "organizations", orgId, "members", uid), memberData);
    } catch {
      // Migration write blocked — return routing doc as fallback so login still works.
      return { ...routing };
    }
    return { ...routing, ...memberData };
  }

  const member = memberSnap.data();
  // Members doc is authoritative for org-specific fields.
  // Routing doc is authoritative for device/global fields.
  return {
    ...member,
    orgId: routing.orgId,
    agreedToTermsVersion: routing.agreedToTermsVersion,
    agreedToTermsAt: routing.agreedToTermsAt,
    fcmToken: routing.fcmToken,
    createdAt: routing.createdAt || member.createdAt,
  };
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  // Prevents onAuthStateChanged from racing login/register's own setProfile calls
  const manualAuthInProgress = useRef(false);
  // Tracks which uid we've already attempted a recovery load for (prevents infinite retry)
  const recoveryAttemptedFor = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          setUser(firebaseUser);
          if (!manualAuthInProgress.current) {
            setProfileLoading(true);
            try {
              const p = await loadFullProfile(firebaseUser.uid);
              // Only overwrite profile if a real value was returned.
              // A null result (doc not found) must not wipe out a valid
              // profile that was already set by login() — e.g. when a
              // second onAuthStateChanged fires after manualAuth clears.
              if (p !== null) setProfile(p);
            } finally {
              setProfileLoading(false);
            }
          }
        } else {
          setUser(null);
          setProfile(null);
          recoveryAttemptedFor.current = null;
        }
      } finally {
        setAuthLoading(false);
      }
    });
    return unsub;
  }, []);

  // Recovery: if user is authenticated but profile is still null after all loading
  // completes (e.g. loadFullProfile returned null during manual login), retry ONCE.
  // The recoveryAttemptedFor ref prevents an infinite retry loop if Firestore
  // keeps returning null.
  useEffect(() => {
    if (authLoading || !user || profile !== null || profileLoading || manualAuthInProgress.current) return;
    if (recoveryAttemptedFor.current === user.uid) return;
    recoveryAttemptedFor.current = user.uid;
    let active = true;
    setProfileLoading(true);
    loadFullProfile(user.uid)
      .then(p => { if (active && p !== null) setProfile(p); })
      .catch(() => {})
      .finally(() => { if (active) setProfileLoading(false); });
    return () => { active = false; };
  }, [user, profile, authLoading]); // eslint-disable-line

  const isSuperUser = profile?.role === "superuser";
  const isAdmin = profile?.role === "admin" || isSuperUser;

  const login = useCallback(async (email, password) => {
    manualAuthInProgress.current = true;
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Hold the loading splash up while we read Firestore — prevents the
      // login form from briefly re-appearing between the onAuthStateChanged
      // fire (which sets user but not profile) and setProfile below.
      setProfileLoading(true);
      let profileData = await loadFullProfile(cred.user.uid);

      // Auto-upgrade: org creator with stale role:"admin" → promote to superuser.
      if (profileData?.role === "admin" && profileData?.orgId) {
        try {
          const orgSnap = await getDoc(doc(db, "organizations", profileData.orgId));
          if (orgSnap.exists() && orgSnap.data().createdBy === cred.user.uid) {
            await updateDoc(
              doc(db, "organizations", profileData.orgId, "members", cred.user.uid),
              { role: "superuser" }
            );
            profileData = { ...profileData, role: "superuser" };
          }
        } catch { /* org read failed, proceed with existing role */ }
      }

      setUser(cred.user);

      if (profileData !== null) {
        setProfile(profileData);
      } else {
        // First read returned null — retry once before giving up.
        const retryData = await loadFullProfile(cred.user.uid);
        if (retryData !== null) {
          setProfile(retryData);
        } else {
          // Both attempts returned null — user doc genuinely missing.
          // Throw so AuthPage surfaces an error instead of silently
          // leaving the user stuck on the login screen.
          throw new Error("Account data not found. Your account may have been removed, or please try again.");
        }
      }

      return cred.user;
    } finally {
      setProfileLoading(false);
      manualAuthInProgress.current = false;
    }
  }, []);

  const register = useCallback(async (email, password, displayName, orgMode, orgName, orgId, inviteToken = "") => {
    manualAuthInProgress.current = true;
    try {
      let finalOrgId = orgId;
      let orgStatus = "pending";
      let role = "user";

      // Validate invite BEFORE creating the auth user — invite GET is public (no auth needed).
      // Org name check must happen AFTER auth creation because the organizations read rule
      // requires isSignedIn(); firing it unauthenticated throws "missing or insufficient permissions".
      if (inviteToken) {
        const inviteSnap = await getDoc(doc(db, "invites", inviteToken));
        if (!inviteSnap.exists()) throw new Error("This invite link is invalid.");
        const invite = inviteSnap.data();
        if (invite.used) throw new Error("This invite link has already been used. Ask your admin for a new one.");
        if (invite.email.toLowerCase() !== email.toLowerCase()) {
          throw new Error(`Please register with the email address this invite was sent to (${invite.email}).`);
        }
        finalOrgId = invite.orgId;
      }

      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName });

      if (!inviteToken) {
        // Name check runs after auth — user is now signed in so the read is allowed.
        // If the name is taken, delete the auth account immediately to avoid orphaning it.
        const nameSnap = await getDocs(query(collection(db, "organizations"), where("name", "==", orgName)));
        if (!nameSnap.empty) {
          await cred.user.delete();
          throw new Error("An organization with that name already exists.");
        }
        const orgRef = await addDoc(collection(db, "organizations"), {
          name: orgName,
          createdBy: cred.user.uid,
          createdAt: serverTimestamp(),
        });
        finalOrgId = orgRef.id;
        orgStatus = "approved";
        role = "superuser";
      } else {
        await addDoc(collection(db, "organizations", finalOrgId, "joinRequests"), {
          uid: cred.user.uid,
          displayName,
          email,
          requestedAt: serverTimestamp(),
        });
      }

      // Routing doc — slim, device/global fields only
      const routingDoc = {
        uid: cred.user.uid,
        email,
        orgId: finalOrgId,
        agreedToTermsVersion: TERMS_VERSION,
        agreedToTermsAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      };

      // Members doc — authoritative org-specific profile
      const memberDoc = {
        uid: cred.user.uid,
        email,
        displayName,
        role,
        orgStatus,
        disabled: false,
        createdAt: serverTimestamp(),
      };

      // Batch: write user docs and mark invite used atomically
      const batch = writeBatch(db);
      batch.set(doc(db, "users", cred.user.uid), routingDoc);
      batch.set(doc(db, "organizations", finalOrgId, "members", cred.user.uid), memberDoc);
      if (inviteToken) {
        batch.update(doc(db, "invites", inviteToken), { used: true });
      }
      await batch.commit();

      const profileData = { ...routingDoc, ...memberDoc };
      setUser(cred.user);
      setProfile(profileData);
      return cred.user;
    } finally {
      manualAuthInProgress.current = false;
    }
  }, []);

  const logout = useCallback(() => signOut(auth), []);

  const joinOrg = useCallback(async (targetOrgId) => {
    if (!user || !profile) throw new Error("Not logged in");
    await addDoc(collection(db, "organizations", targetOrgId, "joinRequests"), {
      uid: user.uid,
      displayName: profile.displayName,
      email: user.email,
      requestedAt: serverTimestamp(),
    });
  }, [user, profile]);

  return { user, profile, isAdmin, isSuperUser, authLoading, profileLoading, login, register, logout, joinOrg };
}
